import {
  FormContext,
  FormContextProvider,
  SchemaFieldsWithContextUI,
} from "@sw-sw/lib-form";
import { LayoutTypes, Question } from "@sw-sw/lib-inspection-templates";
import { TooltipContextProvider } from "@sw-sw/lib-ui";
import { set,difference } from "lodash";
import React, { MouseEventHandler, useContext } from "react";
import QuestionGroupContext from "../../../../contexts/QuestionGroupContext";
import TemplateEditorContext from "../../../../contexts/TemplateEditorContext";
import { defaultInspectionTypeOptions, useInspectionTypes, useInspectionTypesByTemplate } from "../../../../hooks/inspectionTypes";
import { FormModal, SubmitHandlerFn } from "../../../Shared/form";
import Loading from "../../../Shared/ResourceIndex/Loading";

export interface IQuestionDetailFormProps {
  title?: string;
  question?: Question;
  questionValues?: Array<any>;
  onCancel: MouseEventHandler;
  onSubmit: SubmitHandlerFn;
  order: number;
  isUsedTemplate?: boolean;
}

const QuestionDetailForm: React.FC<IQuestionDetailFormProps> = ({
  question = null,
  questionValues = null,
  onCancel,
  title = "Edit Question",
  onSubmit = () => Promise.resolve(),
  order,
  isUsedTemplate = false,
}) => {
  const templateEditorContext = useContext(TemplateEditorContext);
  const questionGroupContext = useContext(QuestionGroupContext);
  const allInspectionTypesQuery = useInspectionTypes(); // All available inspection types
  const templateInspectionTypesQuery = useInspectionTypesByTemplate(templateEditorContext.templateId); // Template-specific inspection types

  const getInitialValues = () => {
    if (question && questionValues) {
      const vals: { [key: string]: string | boolean } = {};

      templateEditorContext.questionConfigData.forEach(config => {
        const valueIndex = questionValues
          .map(_ => _.questionFieldId)
          .findIndex(_ => _ === config.id);

        if (valueIndex >= 0) {
          vals[`${config.fieldName}`] = questionValues[valueIndex].value;
        }
      });

      const valueIndex = questionValues.findIndex(_ =>
        Object.keys(_).includes("questionTypeFieldId"),
      );

      if (valueIndex >= 0) {
        vals["options"] = JSON.parse(questionValues[valueIndex].value);
      }

      return {
        ...question,
        ...vals,
      };
    } else {      
      return {
        groupId: questionGroupContext.questionGroup.id,
        sort_order: order,
        features: [],
      };
    }
  };

  const getSchema = (values: Record<string, any>) => {
    const schema = Object.assign({}, templateEditorContext.defaultSchema);

    templateEditorContext.questionConfigData.forEach((item: any) => {
      if (item.dataSource === "QuestionFeature") {
        schema[item.fieldName].style = { display: "none" };
      }

      return item;
    });

    if (
      !values.question_type_id ||
      !templateEditorContext.questionsWithOptions.includes(
        parseInt(values.question_type_id),
      )
    ) {
      delete schema.options;
    }

    if(templateEditorContext.groupedQuestionTypeConfigData[values.question_type_id]) {
      templateEditorContext.groupedQuestionTypeConfigData[values.question_type_id].map((item: any) => {
        schema[item.fieldName].style = { display: "block" };

        return item;
      });
    }
    
    if (
      questionGroupContext.questionGroup.layout_type === LayoutTypes["TABLE"]
    ) {
      delete schema.question_width;
    }

    if (
      questionGroupContext.questionGroup.layout_type === LayoutTypes["FORM"] && values.question_type_id !== 5
    ) {
      delete schema.comment;
    }


    if (
      values.isInspectionType &&
      (values.question_type_id !== 5 ||
        JSON.stringify(values.options) !==
          JSON.stringify(templateInspectionTypesQuery.data!.inspectionTypes))
    ) {
      if (
        !values.options ||
        values.options.length < 1 ||
        difference(values.options, allInspectionTypesQuery.data).length !== 0
      ) {
        if (title === "Edit Question") set(values, 'options', Array.from(new Set([...(values.options || []), ...(templateInspectionTypesQuery.data!.inspectionTypes || []), ...defaultInspectionTypeOptions])));
        else set(values, 'options', allInspectionTypesQuery.data);
      }
      set(values, 'question_type_id', 5); // id of Select Many
    }

    set(schema, 'question_type_id', {
      ...schema.question_type_id,
      disabled: false,
    });
    if (values.options) {
      set(schema, 'options', { ...schema.options, disabled: false });
    }

    if (values.isInspectionType) {
      set(schema, 'options', {
        ...schema.options,
        isInspectionType: true,
        disabled: false,
        options: allInspectionTypesQuery.data, // All available options for the "Add More" dropdown
      });
    }

    if (isUsedTemplate) {
      set(schema, "force_update", { ...schema.force_update, style: { display: "block" } });
    }

    if (!values.precipHistory && !values.sumOfPrecip) {
      delete schema.forecast_days;
    }

    return schema;
  };

  if (allInspectionTypesQuery.isLoading || !allInspectionTypesQuery.data || 
      templateInspectionTypesQuery.isLoading || !templateInspectionTypesQuery.data!.inspectionTypes) {
    return <Loading />;
  }

  return (
    <TooltipContextProvider>
      <FormContextProvider>
        <FormContext.Consumer>
          {formContext => (
            <FormModal
              onCancel={onCancel}
              onSubmit={onSubmit}
              modalProps={{ title: title }}
            >
              <SchemaFieldsWithContextUI
                schema={getSchema(formContext.value)}
                initialFormData={getInitialValues()}
              />
            </FormModal>
          )}
        </FormContext.Consumer>
      </FormContextProvider>
    </TooltipContextProvider>
  );
};

export default QuestionDetailForm;
